import { useSignal } from "@preact/signals";
import type { LoaderArgs, PageArgs } from "bunfx";
import { RedirectError } from "bunfx";
import { useEffect, useRef } from "preact/hooks";
import {
  IcoCheck,
  IcoChevronDown,
  IcoChevronLeft,
  IcoChevronRight,
  IcoChevronUp,
  IcoSpinner,
  IcoX,
} from "../../components/icons";
import {
  BrandedLayout,
  CommentButton,
  CommentThread,
  PriorityItem,
  PrioritySection,
  ReactionBar,
} from "../../components/ui";
import { ErrorDialog } from "../../components/ui/error-dialog";
import { RatingTrendChart } from "../../components/ui/rating-sparkline";
import { decodeMentionsToHtml } from "../../components/ui/rich-text-mention-extension";
import { usePageTitle } from "../../hooks";
import { rpc } from "../../rpc";
import { showError } from "../../stores/errors";
import type {
  BrandColor,
  FormAnswer,
  ReactionSummary,
  User,
  ValueRecognition,
} from "../../types";
import { isAdmin as checkIsAdmin } from "../../types";
import { formatPeriodRangeShort, formatRelativeDate } from "../../utils/format";
import {
  formatWeekLabel,
  getAdjacentWeek,
  getMondayFromWeekKey,
  getWeekKeyFromDate,
} from "../../utils/period-grouping";
import {
  type FullSubmissionData,
  loadFullSubmission,
  type QuestionInfo,
  type ReviewQueueItem,
} from "./review-shared";

// ============================================================================
// Types
// ============================================================================

type FeedPageState = {
  currentUser: User;
  orgId: string;
  orgName: string;
  brandColor: BrandColor;
  weekKey: string;
  weekLabel: string;
  periodLabel: string | null;
  prevWeekKey: string;
  nextWeekKey: string;
  isCurrentWeek: boolean;
  unreviewedQueue: ReviewQueueItem[];
  reviewedQueue: ReviewQueueItem[];
  preloadedSubmissions: Map<string, FullSubmissionData>;
  isOrgAdmin: boolean;
  hasReports: boolean;
  scope: string | null;
  ratingTrends: Record<
    string,
    Record<string, { periodStart: string; rating: number }[]>
  >;
};

// ============================================================================
// Loader
// ============================================================================

export async function load(args: LoaderArgs): Promise<FeedPageState> {
  const orgId = args.params.orgId ?? "";
  const requestedWeek = args.url.searchParams.get("week");
  const scope = args.url.searchParams.get("scope"); // "team" = direct reports only
  const recognitionParam = args.url.searchParams.get("recognition");

  // Stage 1: Validate auth before making data calls
  const [authResult, orgsResult] = await Promise.all([
    rpc.auth.me({}),
    rpc.orgs.list({}),
  ]);

  if (!authResult.user) throw new RedirectError("/login");

  const org = orgsResult.organizations.find((o) => o.id === orgId);
  if (!org) throw new RedirectError("/");

  // If arriving from a recognition notification, resolve to the author's
  // submit page so the user sees just that person's entry (not everyone's feed)
  if (recognitionParam) {
    try {
      const resolved = await rpc.valueRecognitions.resolveViewUrl({
        recognitionId: recognitionParam,
        workspaceId: orgId,
      });
      if (resolved.viewUrl) {
        throw new RedirectError(resolved.viewUrl);
      }
    } catch (err) {
      if (err instanceof RedirectError) throw err;
      // If resolution fails, fall through to normal feed
    }
  }

  const isOrgAdmin = checkIsAdmin(org.role);

  // Default to the server-recommended period (consistent with team page).
  // Holds on the just-ended period until ~65% into the new period so the
  // feed doesn't flip to an empty new week the instant rollover happens.
  const currentWeekKey = getWeekKeyFromDate(new Date());
  let weekKey: string;
  if (requestedWeek) {
    weekKey = requestedWeek;
  } else {
    const reviewPeriod = await rpc.team.getReviewPeriod({
      workspaceId: orgId,
    });
    const defaultPeriod =
      reviewPeriod.defaultPeriodStart ?? reviewPeriod.currentPeriodStart;
    weekKey = defaultPeriod
      ? getWeekKeyFromDate(new Date(defaultPeriod))
      : currentWeekKey;
  }
  const periodStart = getMondayFromWeekKey(weekKey);

  // Stage 2: Data calls (auth validated)
  // Admin-managers default to direct reports; admin-only (no reports) see everyone.
  const effectiveScope =
    scope ?? (isOrgAdmin && org.hasReports ? "team" : null);
  const useAllMembers = isOrgAdmin && effectiveScope !== "team";
  const teamResult = await rpc.team.getMyTeam({
    workspaceId: orgId,
    periodStart: periodStart.toISOString(),
    allMembers: useAllMembers || undefined,
  });

  // Build queues
  const unreviewedQueue: ReviewQueueItem[] = teamResult.team
    .filter((m) => m.latestSubmission && m.latestSubmission.reviewedAt === null)
    .map((m) => ({
      submissionId: m.latestSubmission!.id,
      formId: m.latestSubmission!.formId,
      memberName: m.name || m.email,
      memberProfilePictureUrl: m.profilePictureUrl ?? null,
      formTitle: m.latestSubmission!.formTitle ?? "Check-in",
    }));

  const reviewedQueue: ReviewQueueItem[] = teamResult.team
    .filter((m) => m.latestSubmission && m.latestSubmission.reviewedAt !== null)
    .map((m) => ({
      submissionId: m.latestSubmission!.id,
      formId: m.latestSubmission!.formId,
      memberName: m.name || m.email,
      memberProfilePictureUrl: m.profilePictureUrl ?? null,
      formTitle: m.latestSubmission!.formTitle ?? "Check-in",
    }));

  // If nobody submitted anything, redirect to team page
  if (unreviewedQueue.length === 0 && reviewedQueue.length === 0) {
    throw new RedirectError(`/orgs/${orgId}/team?week=${weekKey}`);
  }

  // Preload first 3 unreviewed submissions
  const toPreload = unreviewedQueue.slice(0, 3);
  const preloadedEntries = await Promise.all(
    toPreload.map(async (item) => {
      try {
        const data = await loadFullSubmission(orgId, item);
        return [item.submissionId, data] as const;
      } catch {
        return null;
      }
    }),
  );

  const preloadedSubmissions = new Map<string, FullSubmissionData>();
  for (const entry of preloadedEntries) {
    if (entry) preloadedSubmissions.set(entry[0], entry[1]);
  }

  // Fetch rating trends for all members with submissions
  const allQueueItems = [...unreviewedQueue, ...reviewedQueue];
  const memberIds = [
    ...new Set(
      teamResult.team.filter((m) => m.latestSubmission).map((m) => m.id),
    ),
  ];
  const formId = allQueueItems[0]?.formId;
  let ratingTrends: Record<
    string,
    Record<string, { periodStart: string; rating: number }[]>
  > = {};
  if (formId && memberIds.length > 0) {
    try {
      const trendResult = await rpc.submissions.getRatingTrends({
        workspaceId: orgId,
        formId,
        memberIds: memberIds.slice(0, 50),
        periodCount: 8,
      });
      ratingTrends = trendResult.trends;
    } catch {
      // Non-critical — proceed without trends
    }
  }

  return {
    currentUser: authResult.user as User,
    orgId,
    orgName: org.name,
    brandColor: (org.brandColor ?? "indigo") as BrandColor,
    weekKey,
    weekLabel: formatWeekLabel(weekKey),
    periodLabel: teamResult.formPeriod
      ? formatPeriodRangeShort(
          new Date(teamResult.formPeriod.start),
          new Date(teamResult.formPeriod.end),
        )
      : null,
    prevWeekKey: getAdjacentWeek(weekKey, "prev"),
    nextWeekKey: getAdjacentWeek(weekKey, "next"),
    isCurrentWeek: weekKey === currentWeekKey,
    unreviewedQueue,
    reviewedQueue,
    preloadedSubmissions,
    isOrgAdmin: checkIsAdmin(org.role),
    hasReports: teamResult.hasReports,
    scope,
    ratingTrends,
  };
}

// ============================================================================
// Page Component
// ============================================================================

export function Page({ state }: PageArgs<typeof load>) {
  usePageTitle("Activity");
  const {
    currentUser,
    orgId,
    orgName,
    brandColor,
    weekKey,
    weekLabel,
    prevWeekKey,
    nextWeekKey,
    isCurrentWeek,
    unreviewedQueue: initialUnreviewed,
    reviewedQueue: initialReviewed,
    preloadedSubmissions: initialPreloaded,
    isOrgAdmin,
    hasReports,
    scope: initialScope,
    ratingTrends,
  } = state.value;

  // Submission data cache
  const submissionData = useSignal<Map<string, FullSubmissionData>>(
    new Map(initialPreloaded),
  );
  const loadingIds = useSignal<Set<string>>(new Set());
  const concurrentLoads = useSignal(0);

  // Review state
  const reviewedIds = useSignal<Set<string>>(new Set());
  const undoStack = useSignal<Array<{ submissionId: string }>>([]);
  const showUndoToast = useSignal(false);
  const undoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clean up undo timer on unmount
  useEffect(() => {
    return () => {
      if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    };
  }, []);

  // Comment state
  const openCommentIds = useSignal<Set<string>>(new Set());

  // Collapsed reviewed cards
  const expandedReviewedIds = useSignal<Set<string>>(new Set());

  const totalUnreviewed = initialUnreviewed.length;
  const reviewedCount = reviewedIds.value.size;
  const allCaughtUp = reviewedCount >= totalUnreviewed;

  // Load a submission if not already loaded
  const loadSubmission = async (
    submissionId: string,
    item: ReviewQueueItem,
  ) => {
    if (
      submissionData.value.has(submissionId) ||
      loadingIds.value.has(submissionId)
    )
      return;

    // Limit concurrent loads to 2
    if (concurrentLoads.value >= 2) return;

    loadingIds.value = new Set([...loadingIds.value, submissionId]);
    concurrentLoads.value += 1;

    try {
      const data = await loadFullSubmission(orgId, item);
      submissionData.value = new Map([
        ...submissionData.value,
        [submissionId, data],
      ]);
    } catch (err) {
      console.error(`Failed to load submission ${submissionId}:`, err);
    } finally {
      const newLoading = new Set(loadingIds.value);
      newLoading.delete(submissionId);
      loadingIds.value = newLoading;
      concurrentLoads.value = Math.max(0, concurrentLoads.value - 1);
    }
  };

  // Mark reviewed (single)
  const handleMarkReviewed = async (submissionId: string) => {
    try {
      await rpc.submissions.markReviewed({ submissionId });
      reviewedIds.value = new Set([...reviewedIds.value, submissionId]);
      undoStack.value = [...undoStack.value, { submissionId }];

      showUndoToast.value = true;
      if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
      undoTimerRef.current = setTimeout(() => {
        showUndoToast.value = false;
      }, 4000);
    } catch (err) {
      showError(
        "Failed to mark reviewed",
        err instanceof Error ? err.message : "Please try again.",
      );
    }
  };

  // Mark all as reviewed
  const markingAll = useSignal(false);
  const handleMarkAllReviewed = async () => {
    if (markingAll.value) return;
    const toMark = initialUnreviewed.filter(
      (item) => !reviewedIds.value.has(item.submissionId),
    );
    if (toMark.length === 0) return;

    markingAll.value = true;
    const newReviewed = new Set(reviewedIds.value);
    const newUndoEntries: Array<{ submissionId: string }> = [];
    let failCount = 0;

    // Mark in parallel with concurrency limit of 3
    const batch = async (items: ReviewQueueItem[]) => {
      await Promise.all(
        items.map(async (item) => {
          try {
            await rpc.submissions.markReviewed({
              submissionId: item.submissionId,
            });
            newReviewed.add(item.submissionId);
            newUndoEntries.push({ submissionId: item.submissionId });
          } catch {
            failCount++;
          }
        }),
      );
    };

    for (let i = 0; i < toMark.length; i += 3) {
      await batch(toMark.slice(i, i + 3));
    }

    reviewedIds.value = newReviewed;
    undoStack.value = [...undoStack.value, ...newUndoEntries];

    if (failCount > 0) {
      showError(
        "Some reviews failed",
        `${failCount} of ${toMark.length} could not be marked.`,
      );
    }

    showUndoToast.value = true;
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    undoTimerRef.current = setTimeout(() => {
      showUndoToast.value = false;
    }, 5000);
    markingAll.value = false;
  };

  // Undo
  const handleUndo = async () => {
    const stack = undoStack.value;
    if (stack.length === 0) return;

    const entry = stack[stack.length - 1]!;
    undoStack.value = stack.slice(0, -1);
    showUndoToast.value = false;
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);

    try {
      await rpc.submissions.unmarkReviewed({
        submissionId: entry.submissionId,
      });
      const newReviewed = new Set(reviewedIds.value);
      newReviewed.delete(entry.submissionId);
      reviewedIds.value = newReviewed;
    } catch (err) {
      showError(
        "Failed to undo review",
        err instanceof Error ? err.message : "Please try again.",
      );
    }
  };

  // Toggle comment
  const toggleComment = (id: string) => {
    const current = new Set(openCommentIds.value);
    if (current.has(id)) {
      current.delete(id);
    } else {
      current.add(id);
    }
    openCommentIds.value = current;
  };

  // Toggle reviewed card expansion
  const toggleReviewedExpand = (submissionId: string) => {
    const current = new Set(expandedReviewedIds.value);
    if (current.has(submissionId)) {
      current.delete(submissionId);
    } else {
      current.add(submissionId);
    }
    expandedReviewedIds.value = current;
  };

  // Split unreviewed items into not-yet-reviewed and just-reviewed-this-session
  const activeUnreviewed = initialUnreviewed.filter(
    (item) => !reviewedIds.value.has(item.submissionId),
  );
  const justReviewed = initialUnreviewed.filter((item) =>
    reviewedIds.value.has(item.submissionId),
  );
  // All reviewed = initially reviewed + just reviewed
  const allReviewed = [...justReviewed, ...initialReviewed];

  return (
    <BrandedLayout
      user={currentUser}
      orgId={orgId}
      orgName={orgName}
      brandColor={brandColor}
      activeTab="team"
      isAdmin={isOrgAdmin}
      hasReports={hasReports}
    >
      <ErrorDialog />

      <div class="max-w-3xl mx-auto">
        {/* Header */}
        <div class="flex items-center justify-between mb-6">
          <div>
            <div class="flex items-center gap-3 mb-1">
              <a
                href={`/orgs/${orgId}/team?week=${weekKey}`}
                class="inline-flex items-center gap-1 text-sm font-medium text-text-muted hover:text-text transition-colors"
              >
                <IcoChevronLeft class="size-4" />
                Team
              </a>
              <span class="text-border">|</span>
              <a
                href={`/orgs/${orgId}/review?week=${weekKey}${initialScope ? `&scope=${initialScope}` : ""}`}
                class="text-sm font-medium text-text-muted hover:text-text transition-colors"
              >
                Keyboard Mode
              </a>
            </div>
            <h1 class="text-2xl font-bold text-text">Review Feed</h1>
            <p class="text-text-muted mt-1">
              {state.value.periodLabel || weekLabel}
            </p>
          </div>

          <div class="flex items-center gap-4">
            {/* Progress */}
            <div class="text-right">
              <p class="text-sm font-semibold text-text tabular-nums">
                {reviewedCount + initialReviewed.length}/
                {totalUnreviewed + initialReviewed.length} reviewed
              </p>
              {totalUnreviewed > 0 && (
                <div class="w-24 h-1.5 bg-surface-muted rounded-full overflow-hidden mt-1">
                  <div
                    class="h-full bg-[var(--brand-500)] rounded-full transition-all duration-300"
                    style={{
                      width: `${Math.round(((reviewedCount + initialReviewed.length) / (totalUnreviewed + initialReviewed.length)) * 100)}%`,
                    }}
                  />
                </div>
              )}
            </div>

            {/* Week nav */}
            <div class="flex items-center gap-1 bg-surface-muted rounded-lg p-1">
              <a
                href={`/orgs/${orgId}/feed?week=${prevWeekKey}`}
                class="p-2 rounded-md hover:bg-surface transition-colors"
                title="Previous week"
              >
                <IcoChevronLeft class="size-5 text-text-muted" />
              </a>
              {!isCurrentWeek && (
                <a
                  href={`/orgs/${orgId}/feed`}
                  class="px-3 py-1.5 text-sm font-medium text-text-muted hover:text-text hover:bg-surface rounded-md transition-colors"
                >
                  Today
                </a>
              )}
              <a
                href={`/orgs/${orgId}/feed?week=${nextWeekKey}`}
                class={`p-2 rounded-md transition-colors ${
                  isCurrentWeek
                    ? "opacity-50 cursor-not-allowed"
                    : "hover:bg-surface"
                }`}
                title="Next week"
                onClick={
                  isCurrentWeek ? (e: Event) => e.preventDefault() : undefined
                }
              >
                <IcoChevronRight class="size-5 text-text-muted" />
              </a>
            </div>
          </div>
        </div>

        {/* All caught up banner */}
        {allCaughtUp && totalUnreviewed > 0 && (
          <div class="flex items-center justify-center gap-3 p-6 rounded-xl border-2 border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950/30 mb-6">
            <div class="size-10 rounded-full bg-green-100 dark:bg-green-900/50 flex items-center justify-center">
              <IcoCheck class="size-5 text-green-600 dark:text-green-400" />
            </div>
            <span class="text-lg font-medium text-green-700 dark:text-green-400">
              All caught up! You've reviewed all check-ins for this week.
            </span>
          </div>
        )}

        {/* Unreviewed Feed */}
        {activeUnreviewed.length > 0 && (
          <section class="mb-8">
            <h2 class="text-sm font-semibold text-text-muted uppercase tracking-wide mb-4">
              Needs Review ({activeUnreviewed.length})
            </h2>
            <div class="space-y-4">
              {activeUnreviewed.map((item) => (
                <FeedCardLoader
                  key={item.submissionId}
                  item={item}
                  orgId={orgId}
                  submissionData={submissionData.value}
                  onLoad={loadSubmission}
                  onMarkReviewed={handleMarkReviewed}
                  openCommentIds={openCommentIds.value}
                  onToggleComment={toggleComment}
                  ratingTrends={ratingTrends}
                />
              ))}
            </div>

            {/* Mark All as Reviewed */}
            {activeUnreviewed.length > 1 && (
              <div class="mt-6 flex justify-center">
                <button
                  type="button"
                  onClick={handleMarkAllReviewed}
                  disabled={markingAll.value}
                  class="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold bg-[var(--brand-600)] text-white hover:bg-[var(--brand-700)] transition-colors disabled:opacity-50 shadow-sm"
                >
                  {markingAll.value ? (
                    <IcoSpinner class="size-4 animate-spin" />
                  ) : (
                    <IcoCheck class="size-4" />
                  )}
                  Mark All as Reviewed ({activeUnreviewed.length})
                </button>
              </div>
            )}
          </section>
        )}

        {/* Reviewed Section */}
        {allReviewed.length > 0 && (
          <section class="mb-8">
            <h2 class="text-sm font-semibold text-text-muted uppercase tracking-wide mb-4">
              Reviewed ({allReviewed.length})
            </h2>
            <div class="space-y-3">
              {allReviewed.map((item) => (
                <ReviewedCard
                  key={item.submissionId}
                  item={item}
                  orgId={orgId}
                  isExpanded={expandedReviewedIds.value.has(item.submissionId)}
                  onToggleExpand={() => toggleReviewedExpand(item.submissionId)}
                  submissionData={submissionData.value}
                  loadingIds={loadingIds.value}
                  onLoad={loadSubmission}
                  openCommentIds={openCommentIds.value}
                  onToggleComment={toggleComment}
                  ratingTrends={ratingTrends}
                />
              ))}
            </div>
          </section>
        )}
      </div>
      {/* end max-w-3xl */}

      {/* Undo Toast */}
      {showUndoToast.value && undoStack.value.length > 0 && (
        <div class="fixed bottom-6 right-6 z-50 animate-fade-in-up">
          <div class="flex items-center gap-3 px-4 py-3 rounded-lg bg-text text-surface shadow-lg">
            <IcoCheck class="size-4 flex-shrink-0" />
            <span class="text-sm font-medium">
              {undoStack.value.length > 1
                ? `Marked ${undoStack.value.length} as reviewed`
                : "Marked as reviewed"}
            </span>
            <button
              type="button"
              onClick={handleUndo}
              class="text-sm font-semibold underline hover:no-underline ml-2"
            >
              Undo
            </button>
            <button
              type="button"
              onClick={() => {
                showUndoToast.value = false;
              }}
              class="ml-1 opacity-60 hover:opacity-100"
            >
              <IcoX class="size-3.5" />
            </button>
          </div>
        </div>
      )}
    </BrandedLayout>
  );
}

// ============================================================================
// FeedCardLoader — handles lazy loading via IntersectionObserver
// ============================================================================

type RatingTrends = Record<
  string,
  Record<string, { periodStart: string; rating: number }[]>
>;

function FeedCardLoader({
  item,
  orgId,
  submissionData,
  onLoad,
  onMarkReviewed,
  openCommentIds,
  onToggleComment,
  ratingTrends,
}: {
  item: ReviewQueueItem;
  orgId: string;
  submissionData: Map<string, FullSubmissionData>;
  onLoad: (id: string, item: ReviewQueueItem) => void;
  onMarkReviewed: (submissionId: string) => void;
  openCommentIds: Set<string>;
  onToggleComment: (id: string) => void;
  ratingTrends: RatingTrends;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const data = submissionData.get(item.submissionId);

  // Set up IntersectionObserver for lazy loading
  // Don't disconnect until data is actually loaded — if concurrency limit rejects
  // the load, the observer will retry on next intersection
  useEffect(() => {
    if (data) return; // Already loaded, no need for observer
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          onLoad(item.submissionId, item);
        }
      },
      { rootMargin: "200px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [data, item.submissionId]);

  if (data) {
    return (
      <FeedCard
        data={data}
        item={item}
        orgId={orgId}
        onMarkReviewed={onMarkReviewed}
        openCommentIds={openCommentIds}
        onToggleComment={onToggleComment}
        ratingTrends={ratingTrends}
      />
    );
  }

  return (
    <div ref={ref}>
      <SkeletonCard item={item} />
    </div>
  );
}

// ============================================================================
// SkeletonCard
// ============================================================================

function SkeletonCard({ item }: { item: ReviewQueueItem }) {
  return (
    <article class="rounded-xl border border-border bg-surface overflow-hidden">
      {/* Header with real data */}
      <div class="flex items-center gap-4 px-6 py-4 border-b border-border/50">
        {item.memberProfilePictureUrl ? (
          <img
            src={item.memberProfilePictureUrl}
            alt=""
            class="size-10 rounded-full object-cover"
          />
        ) : (
          <div class="size-10 rounded-full bg-surface-muted flex items-center justify-center text-sm font-semibold text-text-muted">
            {item.memberName.charAt(0).toUpperCase()}
          </div>
        )}
        <div class="flex-1 min-w-0">
          <p class="font-medium text-text truncate">{item.memberName}</p>
          <p class="text-sm text-text-muted truncate">{item.formTitle}</p>
        </div>
        <div class="flex items-center gap-2">
          <IcoSpinner class="size-4 text-text-subtle animate-spin" />
        </div>
      </div>
      {/* Skeleton body */}
      <div class="px-6 py-5 space-y-4">
        <div class="space-y-3 animate-pulse">
          <div class="h-4 bg-surface-muted rounded w-3/4" />
          <div class="h-4 bg-surface-muted rounded w-full" />
          <div class="h-4 bg-surface-muted rounded w-5/6" />
        </div>
        <div class="space-y-3 animate-pulse">
          <div class="h-4 bg-surface-muted rounded w-2/3" />
          <div class="h-4 bg-surface-muted rounded w-full" />
        </div>
      </div>
    </article>
  );
}

// ============================================================================
// FeedCard — fully rendered submission card
// ============================================================================

function FeedCard({
  data,
  item,
  orgId,
  onMarkReviewed,
  openCommentIds,
  onToggleComment,
  ratingTrends,
}: {
  data: FullSubmissionData;
  item: ReviewQueueItem;
  orgId: string;
  onMarkReviewed: (submissionId: string) => void;
  openCommentIds: Set<string>;
  onToggleComment: (id: string) => void;
  ratingTrends: RatingTrends;
}) {
  const marking = useSignal(false);
  const showPreviousPriorities = useSignal(false);

  const handleMark = () => {
    if (marking.value) return;
    marking.value = true;
    onMarkReviewed(data.submission.id);
    // Reset marking after a short delay to prevent double-clicks
    setTimeout(() => {
      marking.value = false;
    }, 1000);
  };

  const submittedAt = data.submission.submittedAt
    ? formatRelativeDate(new Date(data.submission.submittedAt))
    : null;

  return (
    <article class="rounded-xl border border-border bg-surface transition-all duration-300">
      {/* Card Header — sticky so name stays visible when scrolling long submissions */}
      <div class="flex items-center gap-4 px-6 py-4 bg-surface-muted/50 dark:bg-surface-muted/30 border-b border-border sticky top-0 z-5 rounded-t-xl backdrop-blur-sm">
        {(data.submission.memberProfilePictureUrl ??
        item.memberProfilePictureUrl) ? (
          <img
            src={
              (data.submission.memberProfilePictureUrl ??
                item.memberProfilePictureUrl)!
            }
            alt=""
            class="size-10 rounded-full object-cover"
          />
        ) : (
          <div class="size-10 rounded-full bg-[var(--brand-100)] dark:bg-[var(--brand-900)]/30 flex items-center justify-center text-sm font-semibold text-[var(--brand-600)] dark:text-[var(--brand-400)]">
            {(data.submission.memberName ?? item.memberName)
              .charAt(0)
              .toUpperCase()}
          </div>
        )}
        <div class="flex-1 min-w-0">
          <p class="font-medium text-text truncate">
            {data.submission.memberName ?? item.memberName}
          </p>
          <div class="flex items-center gap-2 text-sm text-text-muted">
            <span class="truncate">{data.form.title || item.formTitle}</span>
            {submittedAt && (
              <>
                <span class="text-border">·</span>
                <span class="whitespace-nowrap">{submittedAt}</span>
              </>
            )}
            {data.submission.managerReviewedBy && (
              <>
                <span class="text-border">·</span>
                <span class="whitespace-nowrap text-green-600 dark:text-green-400">
                  <IcoCheck class="size-3 inline -mt-0.5" />{" "}
                  {data.submission.managerReviewedBy} reviewed
                </span>
              </>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={handleMark}
          disabled={marking.value}
          class="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-[var(--brand-600)] text-white hover:bg-[var(--brand-700)] transition-colors disabled:opacity-50 flex-shrink-0"
        >
          {marking.value ? (
            <IcoSpinner class="size-4 animate-spin" />
          ) : (
            <IcoCheck class="size-4" />
          )}
          Mark Reviewed
        </button>
      </div>

      {/* Card Body */}
      <div class="px-6 py-5">
        {/* Rating answers first */}
        {(() => {
          const ratingQs = data.questions.filter((q) => q.type === "rating");
          const otherQs = data.questions.filter((q) => q.type !== "rating");
          const memberId = data.submission.memberId;

          const renderAnswer = (question: (typeof data.questions)[0]) => {
            const answer =
              data.submission.answers.find(
                (a) => a.questionId === question.id,
              ) ?? null;
            const commentKey = `feed-answer-${data.submission.id}-${question.id}`;
            const trend =
              memberId && question.type === "rating"
                ? ratingTrends[memberId]?.[question.id]
                : undefined;
            return (
              <div key={question.id}>
                <FeedAnswerCard
                  question={question}
                  answer={answer}
                  workspaceId={orgId}
                  currentMemberId={data.currentMemberId}
                  initialReactions={
                    answer?.id ? data.answerReactions[answer.id] : undefined
                  }
                  commentCount={
                    answer?.id ? (data.answerCommentCounts[answer.id] ?? 0) : 0
                  }
                  isCommentOpen={openCommentIds.has(commentKey)}
                  onToggleComment={() => onToggleComment(commentKey)}
                  ratingTrend={trend}
                />
              </div>
            );
          };

          return (
            <>
              {ratingQs.length > 0 && (
                <div class="space-y-4 mb-5">{ratingQs.map(renderAnswer)}</div>
              )}

              {/* Values Recognition — right after rating */}
              {data.recognitions.length > 0 && (
                <div class="space-y-3 mb-5">
                  <h3 class="text-sm font-semibold text-text-muted uppercase tracking-wide">
                    Values Recognition
                  </h3>
                  {data.recognitions.map((rec) => (
                    <FeedRecognitionCard
                      key={rec.id}
                      recognition={rec}
                      orgId={orgId}
                      currentMemberId={data.currentMemberId}
                      commentCount={data.recognitionCommentCounts[rec.id] ?? 0}
                      initialReactions={data.recognitionReactions[rec.id]}
                    />
                  ))}
                </div>
              )}

              {otherQs.length > 0 && (
                <div class="space-y-4 mb-5">{otherQs.map(renderAnswer)}</div>
              )}
            </>
          );
        })()}

        {/* Current priorities */}
        {data.priorities && data.priorities.currentPeriod.length > 0 && (
          <div class="mb-5">
            <PrioritySection
              title={`${data.submission.memberName ?? "Their"}'s Current Priorities`}
              description="What they're working on this period"
            >
              {data.priorities.currentPeriod.map((priority) => (
                <PriorityItem
                  key={priority.id}
                  priority={priority}
                  readOnly={true}
                  workspaceId={orgId}
                  currentMemberId={data.currentMemberId}
                  commentCount={data.priorityCommentCounts[priority.id] ?? 0}
                  reactions={data.priorityReactions[priority.id]}
                />
              ))}
            </PrioritySection>
          </div>
        )}

        {/* Previous priorities (collapsed by default) */}
        {data.priorities && data.priorities.previousPeriod.length > 0 && (
          <div class="mb-5">
            <button
              type="button"
              onClick={() => {
                showPreviousPriorities.value = !showPreviousPriorities.value;
              }}
              class="flex items-center gap-2 text-sm font-medium text-text-muted hover:text-text transition-colors mb-2"
            >
              {showPreviousPriorities.value ? (
                <IcoChevronUp class="size-4" />
              ) : (
                <IcoChevronDown class="size-4" />
              )}
              Previous Priorities ({data.priorities.previousPeriod.length})
            </button>
            {showPreviousPriorities.value && (
              <PrioritySection title="" description="">
                {data.priorities.previousPeriod.map((priority) => (
                  <PriorityItem
                    key={priority.id}
                    priority={priority}
                    isFromPrevious={true}
                    readOnly={true}
                    workspaceId={orgId}
                    currentMemberId={data.currentMemberId}
                    commentCount={data.priorityCommentCounts[priority.id] ?? 0}
                    reactions={data.priorityReactions[priority.id]}
                  />
                ))}
              </PrioritySection>
            )}
          </div>
        )}

        {/* Bottom Mark Reviewed button */}
        <div class="mt-5 pt-4 border-t border-border/40">
          <button
            type="button"
            onClick={handleMark}
            disabled={marking.value}
            class="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border border-[var(--brand-600)] text-[var(--brand-600)] hover:bg-[var(--brand-50)] dark:hover:bg-[var(--brand-900)]/20 transition-colors disabled:opacity-50"
          >
            {marking.value ? (
              <IcoSpinner class="size-4 animate-spin" />
            ) : (
              <IcoCheck class="size-4" />
            )}
            Mark Reviewed
          </button>
        </div>
      </div>
    </article>
  );
}

// ============================================================================
// ReviewedCard — collapsed reviewed card with expand toggle
// ============================================================================

function ReviewedCard({
  item,
  orgId,
  isExpanded,
  onToggleExpand,
  submissionData,
  loadingIds,
  onLoad,
  openCommentIds,
  onToggleComment,
  ratingTrends,
}: {
  item: ReviewQueueItem;
  orgId: string;
  isExpanded: boolean;
  onToggleExpand: () => void;
  submissionData: Map<string, FullSubmissionData>;
  loadingIds: Set<string>;
  onLoad: (id: string, item: ReviewQueueItem) => void;
  openCommentIds: Set<string>;
  onToggleComment: (id: string) => void;
  ratingTrends: RatingTrends;
}) {
  const data = submissionData.get(item.submissionId);
  const ref = useRef<HTMLDivElement>(null);

  // Lazy load when expanded and data not yet loaded
  useEffect(() => {
    if (isExpanded && !data && !loadingIds.has(item.submissionId)) {
      onLoad(item.submissionId, item);
    }
  }, [isExpanded, data, item.submissionId]);

  return (
    <article
      ref={ref}
      class="rounded-xl border border-border bg-surface overflow-hidden opacity-60 hover:opacity-80 transition-all duration-300"
    >
      {/* Collapsed header */}
      <button
        type="button"
        onClick={onToggleExpand}
        class="flex items-center gap-4 w-full px-6 py-3 text-left hover:bg-surface-hover transition-colors"
      >
        <div class="size-8 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0">
          <IcoCheck class="size-4 text-green-600 dark:text-green-400" />
        </div>
        <div class="flex-1 min-w-0">
          <p class="font-medium text-text truncate text-sm">
            {item.memberName}
          </p>
          <p class="text-xs text-text-muted truncate">{item.formTitle}</p>
        </div>
        <span class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
          <IcoCheck class="size-3.5" />
          Reviewed
        </span>
        {isExpanded ? (
          <IcoChevronUp class="size-4 text-text-muted flex-shrink-0" />
        ) : (
          <IcoChevronDown class="size-4 text-text-muted flex-shrink-0" />
        )}
      </button>

      {/* Expanded body */}
      {isExpanded && (
        <div class="border-t border-border/50">
          {data ? (
            <div class="px-6 py-5">
              {/* Rating → Recognition → Other answers → Priorities */}
              {(() => {
                const ratingQs = data.questions.filter(
                  (q) => q.type === "rating",
                );
                const otherQs = data.questions.filter(
                  (q) => q.type !== "rating",
                );
                const memberId = data.submission.memberId;

                const renderAnswer = (question: (typeof data.questions)[0]) => {
                  const answer =
                    data.submission.answers.find(
                      (a) => a.questionId === question.id,
                    ) ?? null;
                  const commentKey = `feed-answer-${data.submission.id}-${question.id}`;
                  const trend =
                    memberId && question.type === "rating"
                      ? ratingTrends[memberId]?.[question.id]
                      : undefined;
                  return (
                    <div key={question.id} class="py-5 first:pt-0 last:pb-0">
                      <FeedAnswerCard
                        question={question}
                        answer={answer}
                        workspaceId={orgId}
                        currentMemberId={data.currentMemberId}
                        initialReactions={
                          answer?.id
                            ? data.answerReactions[answer.id]
                            : undefined
                        }
                        commentCount={
                          answer?.id
                            ? (data.answerCommentCounts[answer.id] ?? 0)
                            : 0
                        }
                        isCommentOpen={openCommentIds.has(commentKey)}
                        onToggleComment={() => onToggleComment(commentKey)}
                        ratingTrend={trend}
                      />
                    </div>
                  );
                };

                return (
                  <>
                    {ratingQs.length > 0 && (
                      <div class="divide-y divide-border/60 mb-4">
                        {ratingQs.map(renderAnswer)}
                      </div>
                    )}

                    {data.recognitions.length > 0 && (
                      <div class="space-y-3 mb-4">
                        {data.recognitions.map((rec) => (
                          <FeedRecognitionCard
                            key={rec.id}
                            recognition={rec}
                            orgId={orgId}
                            currentMemberId={data.currentMemberId}
                            commentCount={
                              data.recognitionCommentCounts[rec.id] ?? 0
                            }
                            initialReactions={data.recognitionReactions[rec.id]}
                          />
                        ))}
                      </div>
                    )}

                    {otherQs.length > 0 && (
                      <div class="divide-y divide-border/60 mb-4">
                        {otherQs.map(renderAnswer)}
                      </div>
                    )}
                  </>
                );
              })()}

              {/* Current priorities */}
              {data.priorities && data.priorities.currentPeriod.length > 0 && (
                <div class="mb-5">
                  <PrioritySection
                    title={`${data.submission.memberName ?? "Their"}'s Current Priorities`}
                    description=""
                  >
                    {data.priorities.currentPeriod.map((priority) => (
                      <PriorityItem
                        key={priority.id}
                        priority={priority}
                        readOnly={true}
                        workspaceId={orgId}
                        currentMemberId={data.currentMemberId}
                        commentCount={
                          data.priorityCommentCounts[priority.id] ?? 0
                        }
                        reactions={data.priorityReactions[priority.id]}
                      />
                    ))}
                  </PrioritySection>
                </div>
              )}
            </div>
          ) : (
            <div class="px-6 py-5">
              <div class="space-y-3 animate-pulse">
                <div class="h-4 bg-surface-muted rounded w-3/4" />
                <div class="h-4 bg-surface-muted rounded w-full" />
                <div class="h-4 bg-surface-muted rounded w-5/6" />
              </div>
            </div>
          )}
        </div>
      )}
    </article>
  );
}

// RatingSparkline imported from shared component

// ============================================================================
// FeedAnswerCard — answer display with reactions + comments
// ============================================================================

function FeedAnswerCard({
  question,
  answer,
  workspaceId,
  currentMemberId,
  initialReactions,
  commentCount,
  isCommentOpen,
  onToggleComment,
  ratingTrend,
}: {
  question: QuestionInfo;
  answer: FormAnswer | null;
  workspaceId: string;
  currentMemberId: string;
  initialReactions?: ReactionSummary[];
  commentCount: number;
  isCommentOpen: boolean;
  onToggleComment: () => void;
  ratingTrend?: { periodStart: string; rating: number }[];
}) {
  const localCommentCount = useSignal(commentCount);
  const hasCommentsFeature = answer?.id && workspaceId && currentMemberId;

  return (
    <article class="group">
      <h3
        class="text-sm font-semibold text-text-muted mb-2 prose prose-sm max-w-none [&_p]:m-0 [&_p]:text-sm [&_p]:font-semibold [&_p]:text-text-muted"
        dangerouslySetInnerHTML={{ __html: question.title }}
      />

      {question.type === "short_answer" ? (
        answer?.textAnswer ? (
          <div>
            <div
              class="prose prose-base max-w-none text-text [&_p]:m-0 [&_p]:leading-relaxed"
              dangerouslySetInnerHTML={{
                __html: decodeMentionsToHtml(answer.textAnswer),
              }}
            />
          </div>
        ) : (
          <p class="text-text-muted italic text-sm">No response</p>
        )
      ) : answer?.ratingAnswer != null ? (
        <div>
          <RatingTrendChart
            data={
              ratingTrend && ratingTrend.length > 0
                ? ratingTrend
                : [
                    {
                      periodStart: new Date().toISOString(),
                      rating: answer.ratingAnswer,
                    },
                  ]
            }
            max={question.ratingMax}
          />
          {answer?.comment && (
            <p class="mt-2 text-sm text-text-muted italic">{answer.comment}</p>
          )}
        </div>
      ) : (
        <p class="text-text-muted italic text-sm">No response</p>
      )}

      {/* Reaction + Comment on same row */}
      {hasCommentsFeature && (
        <div class="mt-2 flex items-center gap-3">
          <ReactionBar
            workspaceId={workspaceId}
            targetType="form_answer"
            targetId={answer.id!}
            initialReactions={initialReactions}
          />
          <CommentButton
            count={localCommentCount.value}
            isOpen={isCommentOpen}
            onClick={onToggleComment}
          />
        </div>
      )}
      {hasCommentsFeature && isCommentOpen && (
        <div class="mt-2 rounded-lg bg-surface-muted/50 dark:bg-surface-muted/30">
          <div class="p-4">
            <CommentThread
              workspaceId={workspaceId}
              targetType="form_answer"
              targetId={answer.id!}
              currentMemberId={currentMemberId}
              autoFocus
              onCommentCountChange={(count) => {
                localCommentCount.value = count;
              }}
            />
          </div>
        </div>
      )}
    </article>
  );
}

// ============================================================================
// FeedRecognitionCard — stateful recognition card with comments
// ============================================================================

function FeedRecognitionCard({
  recognition,
  orgId,
  currentMemberId,
  commentCount: initialCommentCount,
  initialReactions,
}: {
  recognition: ValueRecognition;
  orgId: string;
  currentMemberId: string;
  commentCount: number;
  initialReactions?: ReactionSummary[];
}) {
  const showComments = useSignal(false);
  const localCommentCount = useSignal(initialCommentCount);

  useEffect(() => {
    localCommentCount.value = initialCommentCount;
  }, [initialCommentCount]);

  return (
    <div
      id={`recognition-${recognition.id}`}
      class="rounded-xl border border-border bg-surface"
    >
      <div class="p-4 space-y-2">
        <div class="flex flex-wrap items-center gap-1.5 text-sm">
          {recognition.author.profilePictureUrl ? (
            <img
              src={recognition.author.profilePictureUrl}
              alt=""
              class="size-5 rounded-full object-cover"
            />
          ) : (
            <span class="size-5 rounded-full bg-surface-muted flex items-center justify-center text-[10px] font-semibold text-text-muted">
              {recognition.author.name?.charAt(0).toUpperCase()}
            </span>
          )}
          <span class="font-medium text-text">{recognition.author.name}</span>
          <span class="text-text-muted">recognized</span>
          {recognition.recipients.map((r) => (
            <span
              key={r.memberId}
              class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-surface-hover text-text"
            >
              {r.profilePictureUrl ? (
                <img
                  src={r.profilePictureUrl}
                  alt=""
                  class="size-4 rounded-full object-cover"
                />
              ) : (
                <span class="size-4 rounded-full bg-surface-muted flex items-center justify-center text-[9px] font-semibold text-text-muted">
                  {r.name?.charAt(0).toUpperCase()}
                </span>
              )}
              {r.name}
            </span>
          ))}
        </div>
        <div class="flex flex-wrap gap-1.5">
          {recognition.values.map((v) => (
            <span
              key={v.id}
              class="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-[var(--brand-100)] text-[var(--brand-700)]"
            >
              {v.name}
            </span>
          ))}
        </div>
        {recognition.message && (
          <p class="text-sm text-text whitespace-pre-wrap">
            {recognition.message}
          </p>
        )}
      </div>
      <div class="px-4 pb-3 flex items-center gap-3">
        <ReactionBar
          workspaceId={orgId}
          targetType="value_recognition"
          targetId={recognition.id}
          initialReactions={initialReactions}
        />
        <CommentButton
          count={localCommentCount.value}
          isOpen={showComments.value}
          onClick={() => {
            showComments.value = !showComments.value;
          }}
        />
      </div>
      {showComments.value && (
        <div class="border-t border-border px-4 py-3">
          <CommentThread
            workspaceId={orgId}
            targetType="value_recognition"
            targetId={recognition.id}
            currentMemberId={currentMemberId}
            onCommentCountChange={(count) => {
              localCommentCount.value = count;
            }}
          />
        </div>
      )}
    </div>
  );
}
